import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useRef, useState} from 'react';

import {DataFrameNameState} from 'analysis-tool/data-frame-creation/DataFrameCreationTypes';
import {FormState} from 'analysis-tool/data-frame-creation/schemas/RegistrySchemaDataFrameCreationTypes';
import RegistrySchemasStepperForm from 'analysis-tool/data-frame-creation/schemas/RegistrySchemasStepperForm';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {setValueInConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import useQueryApiIntrospection from 'query-api/useQueryApiIntrospection';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  DEFAULT_QUERY_FORM_STATE,
  buildQueryStringFromFormState,
  notifyIfFieldCountExceeded,
} from './LookUpQueryUtils';
import {useDataFrameNameStateBridge} from './useDataFrameNameStateBridge';

export default function RegistrySchemaQueryEditor({
  config,
  onChangeConfig,
}: CustomComponentEditorProps): JSX.Element {
  const [queryFormState, setQueryFormState] = useState<FormState>(DEFAULT_QUERY_FORM_STATE);
  const queryApiIntrospection = useQueryApiIntrospection();
  // Ref to suppress the redundant setDataFrameNameState call that would race with
  // handleSetQueryFormState when both fire in the same synchronous event handler.
  const suppressNameUpdateRef = useRef(false);

  const {dataFrameNameState, handleSetDataFrameNameState} = useDataFrameNameStateBridge(
    config,
    onChangeConfig
  );

  const handleSetQueryFormState = useCallback(
    (newFormState: FormState, newDataFrameName?: string) => {
      if (notifyIfFieldCountExceeded(newFormState)) {
        return;
      }
      setQueryFormState(newFormState);
      const queryString = buildQueryStringFromFormState(newFormState);
      if (queryString == null) {
        // Even without a valid query, we may need to set the dataFrameName
        if (newDataFrameName != null) {
          onChangeConfig(config, ['dataFrameName'], newDataFrameName);
          suppressNameUpdateRef.current = true;
        }
        return;
      }
      let updatedConfig = setValueInConfig(config, ['query'], queryString);
      updatedConfig = setValueInConfig(updatedConfig, ['queryDescription'], '');
      // Atomically set dataFrameName along with query to avoid a race condition where
      // separate onChangeConfig calls in the same event handler overwrite each other
      // due to stale closures (both capturing the same pre-update config).
      if (newDataFrameName != null) {
        updatedConfig = setValueInConfig(updatedConfig, ['dataFrameName'], newDataFrameName);
        suppressNameUpdateRef.current = true;
      }
      onChangeConfig(updatedConfig, ['queryDescription'], '');
    },
    [config, onChangeConfig]
  );

  // Guarded version of setDataFrameNameState that skips the update when the name was
  // already set atomically via handleSetQueryFormState in the same event handler.
  const handleSetDataFrameNameStateGuarded = useCallback(
    (newState: DataFrameNameState) => {
      if (suppressNameUpdateRef.current) {
        suppressNameUpdateRef.current = false;
        return;
      }
      handleSetDataFrameNameState(newState);
    },
    [handleSetDataFrameNameState]
  );

  if (queryApiIntrospection.status === QueryResultType.ERROR) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading registry schemas." />;
  }
  if (queryApiIntrospection.status === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <div className={css(styles.wrapper)}>
      <RegistrySchemasStepperForm
        queryApiIntrospection={queryApiIntrospection}
        queryFormState={queryFormState}
        setQueryFormState={handleSetQueryFormState}
        setIsManuallyChanged={() => {}}
        dataFrameNameState={dataFrameNameState}
        setDataFrameNameState={handleSetDataFrameNameStateGuarded}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingBottom: '5px',
  },
});
